import { useCallback } from "react"
import { useDropzone } from "react-dropzone"
import styled, { useTheme } from "styled-components"
import { MaterialIcon } from "../shared/MaterialIcon"

export const FileDropzone = ({
    title,
    header = 'Velg fil eller dra og slipp her.',
    description,
    helptext,
    onUpload,
    acceptedExtensions = [],
}) => {

    const onDrop = useCallback(acceptedFiles => {
        onUpload(acceptedFiles);
        // eslint-disable-next-line
    }, []);

    const extensionToMime = {
        txt: 'text/plain',
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        csv: 'text/csv',
        tit: 'application/octet-stream',
        nyl: 'application/octet-stream',
    };

    const accept = Object.fromEntries(
        acceptedExtensions.map(ext => [
            extensionToMime[ext],
            [`.${ext}`]
        ])
    );

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        maxFiles: 1,    
        accept
    })

    const GlobalTheme = useTheme();
    const color = encodeURIComponent(GlobalTheme.colors.mutedForeground);

    const backgroundBorderUrl = `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='${color}' stroke-width='2' stroke-dasharray='10%2c 8' stroke-dashoffset='5' stroke-linecap='square'/%3e%3c/svg%3e")`

    return (
        <StyledFileDropzone
            className={isDragActive ? 'dropzoneActive' : null}
            $borderImageUrl = {backgroundBorderUrl}
        >
            {title ? <span className="dropzoneTitle">{title}</span> : null}
            <div className="dropzoneContainer" {...getRootProps()}>
                <input {...getInputProps()}/>
                <div className="dropzoneContent">
                    <div className="dropzoneButton">
                        <MaterialIcon
                            name='upload'
                            weight={300}
                        />
                        Last opp
                    </div>
                    <div className="dropzoneHeader">
                        {header}
                    </div>
                    {
                        description ?
                        <div className="dropzoneDescription">
                            {description}
                        </div>
                        : null
                    }
                </div>
            </div>
            {
                helptext ? 
                <div className="dropzoneHelptext">
                    {helptext}
                </div>
                : null
            }
        </StyledFileDropzone>
    )
}

const StyledFileDropzone = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacing.sm};
    transition-duration: 110ms;
    cursor: pointer;

    &.dropzoneActive div.dropzoneContent
    {
        outline: 1px solid ${({theme}) => theme.colors.neutralPrimaryForeground};
        background-image: none;
        filter: drop-shadow(0 2px 2px rgba( ${({theme}) => theme.colors.neutralSecondaryForegroundRGB}, 0.15));
    }

    div.dropzoneContent
    {
        padding: ${({theme}) => theme.spacing.lg} ${({theme}) => theme.spacing.lg};
        width: 100%;
        border-radius: ${({theme}) => theme.cornerRadius.md};
        background-image: ${({$borderImageUrl}) => $borderImageUrl};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${({theme}) => theme.spacing.md};
        background-color: ${({theme}) => theme.colors.neutralInputBackground};
        filter: drop-shadow(0 2px 2px rgba( ${({theme}) => theme.colors.neutralSecondaryForegroundRGB}, 0.05));
        transition-duration: 110ms;

        div.dropzoneButton
        {
            border: 1px solid ${({theme}) => theme.colors.neutralPrimaryForeground};
            width: fit-content;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${({theme}) => theme.spacing.md};
            padding: ${({theme}) => theme.spacing.sm} ${({theme}) => theme.spacing.md};
            border-radius: ${({theme}) => theme.cornerRadius.sm};
            background-color: ${({theme}) => theme.colors.neutralPrimaryBackground};
        }

        div.dropzoneHeader
        {
            font-weight: 600;
            text-align: center;
        }

        div.dropzoneDescription
        {
            color: ${({theme}) => theme.colors.mutedForeground};
            text-align: center;
        }

        
    }

    div.dropzoneHelptext
    {
        font-size: ${({theme}) => theme.fontSize.sm};
        color: ${({theme}) => theme.colors.mutedForeground};
        padding-left: ${({theme}) => theme.spacing.md};
        padding-right: ${({theme}) => theme.spacing.md};
    }
`